import { IconCircleCheck, IconCheck, IconX } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import type { OrderStatus } from "../../../../components/common/chip";
import type { TypographyTone } from "../../../../components/common/typography";
import { formatLongDate } from "../../utils/order-status";

interface BannerStyle {
  bg: string;
  border: string;
  iconBg: string;
  icon: ReactNode;
  titleTone: TypographyTone;
  textTone: TypographyTone;
}

const POSITIVE: BannerStyle = {
  bg: "bg-accentGreenTintBg",
  border: "border-accentGreen",
  iconBg: "bg-accentGreen",
  icon: <IconCircleCheck size={26} className="text-bgWhite" />,
  titleTone: "greenDark",
  textTone: "greenDark",
};

const NEGATIVE: BannerStyle = {
  bg: "bg-bgLight",
  border: "border-borderDefault",
  iconBg: "bg-borderDefault",
  icon: <IconX size={26} className="text-textMuted" />,
  titleTone: "dark",
  textTone: "muted",
};

const STYLE: Record<OrderStatus, BannerStyle> = {
  processing: POSITIVE,
  inTransit: POSITIVE,
  delivered: POSITIVE,
  validated: POSITIVE,
  cancelled: NEGATIVE,
  rejected: NEGATIVE,
};

const TITLE: Record<OrderStatus, string> = {
  processing: "Order Processing",
  inTransit: "Order in Transit",
  delivered: "Order Delivered",
  validated: "Order Validated",
  cancelled: "Order Cancelled",
  rejected: "Order Rejected",
};

interface OrderStatusBannerProps {
  status: OrderStatus;
  /** Most recent state-change date (writeDate) — used to render the banner subtitle. */
  statusChangedAt?: string | null;
  /** Estimated delivery date sourced from the dropshipping record. Only populated
   * once the seller has shipped the order, so it only appears on the inTransit banner. */
  estimatedDelivery?: string | null;
  onConfirm?: () => void;
  isConfirming?: boolean;
}

function buildLines({
  status,
  statusChangedAt,
  estimatedDelivery,
}: OrderStatusBannerProps): string[] {
  const eta = estimatedDelivery ? `Estimated Delivery: ${formatLongDate(estimatedDelivery)}` : null;
  const stateDate = statusChangedAt ? formatLongDate(statusChangedAt) : null;

  switch (status) {
    case "processing":
      return ["The seller has received your order and is preparing it for delivery."];
    case "inTransit":
      return [
        stateDate ? `Your order was shipped on ${stateDate}` : null,
        eta,
      ].filter((x): x is string => !!x);
    case "delivered":
      return [stateDate ? `Your order was delivered on ${stateDate}` : "Your order has been delivered."];
    case "validated":
      return ["Your order has been validated."];
    case "cancelled":
      return ["This order has been cancelled.", "Refund will be processed within 5–7 business days."];
    case "rejected":
      return ["This order has been rejected."];
  }
}

export function OrderStatusBanner(props: OrderStatusBannerProps) {
  const { status, onConfirm, isConfirming } = props;
  const style = STYLE[status];
  const lines = buildLines(props);
  const showConfirm = status === "delivered" && !!onConfirm;

  return (
    <div className={`flex items-center gap-4 rounded-2xl border ${style.border} ${style.bg} px-6 py-5`}>
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${style.iconBg}`}>
        {style.icon}
      </div>

      <div className="flex flex-1 flex-col gap-0.5">
        <Heading variant="h3" tone={style.titleTone}>{TITLE[status]}</Heading>
        {lines.map((line) => (
          <Text key={line} variant="body" tone={style.textTone}>{line}</Text>
        ))}
      </div>

      {showConfirm && (
        <Button
          variant="primaryGreen"
          leftIcon={<IconCheck size={16} />}
          onClick={onConfirm}
          disabled={isConfirming}
        >
          {isConfirming ? "Confirming…" : "Confirm Order"}
        </Button>
      )}
    </div>
  );
}
